import { epochSecondsToDate, z, type ZodType } from '@customerio/hydra-validation';
import type { ApiCredential as UIAPIApiCredential } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';
import { nullableEpochSecondsToDate } from '../shared/dates';

/**
 * Tracking API credential (the "Track API key") — a `site_id` + `api_key` pair
 * belonging to one workspace environment. Account-scoped in this layer: the
 * list endpoint (`GET /accounts/{account_id}/api_credentials`) returns every
 * environment's credentials, and the workspace view filters by `environment_id`
 * at read time. Writes are environment-scoped (see mutations.ts), but the
 * cached list lives under the account so it survives workspace switches.
 *
 * `id` is coerced number → string (this layer keys on strings) and the epoch
 * timestamps become `Date`s; everything else (`site_id`, `api_key`,
 * `environment_id`, `name`) passes through.
 */
export type ApiCredential = Omit<UIAPIApiCredential, 'id' | 'created_at' | 'last_used_at'> & {
  id: string;
  created_at: Date;
  last_used_at: Date;
};

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const ApiCredentialFromApi = z.looseObject({
  id: z.number().transform(String),
  created_at: epochSecondsToDate,
  // Wire ships `number | null` (null/absent for never-used) — see nullableEpochSecondsToDate.
  last_used_at: nullableEpochSecondsToDate,
}) as unknown as ZodType<ApiCredential, unknown>;

/** @public */
export const apiCredential = defineEntity<ApiCredential, ApiCredential, void, undefined, 'account'>({
  name: 'api_credential',
  scope: 'account',
  pk: (credential) => credential.id,
  schema: { read: ApiCredentialFromApi },
});

export const apiCredentialKeys = apiCredential.keys;

export function parseApiCredential(raw: UIAPIApiCredential): ApiCredential {
  return apiCredential.parse(raw);
}
